import Link from 'next/link';
import orderBy from 'lodash/orderBy';

import { ConversationWithLastMessage } from 'api/conversation/types';
import { Bot } from 'api/bot/types';
import { useState } from 'react';

import { ConversationSkeleton } from './skeletons/ConversationSkeleton';
import { ConversationItem } from './lists/lists/ConversationItem';
import { reactQuery } from 'lib/reactQuery';
import { useChatStore } from 'store/chat';
import { useAuthStore } from 'store/auth';
import { searchByKeys } from 'utils';
import { useRouter } from 'next/router';
import { Back } from './icons/Back';
import { Search } from './icons/Search';

type Props = {
  bot?: Bot;
};

export const ConversationSidebar = ({ bot }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { pathname } = useRouter();
  const { isLogged } = useAuthStore();
  const { isVisibleConversations } = useChatStore();

  const queryState = reactQuery.getQueryState<ConversationWithLastMessage[]>(['all-conversations']);
  const data: ConversationWithLastMessage[] = queryState?.data || [];

  const filteredData = searchByKeys(data || [], ['bot_attributes.full_name', 'last_message.content'], searchQuery);

  const renderList = () => {
    if (!isLogged && bot) return <ConversationItem bot={bot} />;

    if (!data.length && queryState?.status === 'loading') return <ConversationSkeleton />;

    if (!data.length && queryState?.status === 'error')
      return <p className="text-center text-white text-opacity-30 text-sm p-4">Failed to load conversations.</p>;

    const orderedData = orderBy(
      filteredData,
      (e) => (e.last_message?.created_at ? new Date(e.last_message.created_at).getTime() : 0),
      ['desc']
    );

    return (
      <>
        {orderedData.map((item) => (
          <ConversationItem
            key={item.account_id}
            item={item}
            bot={bot?.id === item.account_id ? bot : undefined}
            mostRecentConversation={orderedData.filter((e) => e.account_id !== item.account_id)?.[0]}
          />
        ))}

        {orderedData.length < 3 && orderedData.length > 0 && (
          <div className="flex flex-col items-center justify-center flex-1 p-4 text-center">
            <p className="text-white text-opacity-40 text-sm mb-3 max-w-[70%] pt-20">
              A bit quiet here, let&apos;s find more
              Krushes to chat with.</p>
            <Link
              href="/"
              className="text-sm px-4 py-2 bg-secondary rounded-lg hover:bg-opacity-50 hover:bg-brand transition-colors"
            >
              Browse More
            </Link>
          </div>
        )}

        {!orderedData.length && queryState?.status === 'success' && (
          <p className="text-center text-white text-opacity-30 text-sm p-4">No conversations found.</p>
        )}
      </>
    );
  };

  const isVisible = pathname === '/chat' ? true : isVisibleConversations;

  return (
    <div
      className={`bg-primary border-r-0 lg:border-r border-primary flex-col h-full absolute left-0 top-0 bottom-0 w-full lg:w-[340px] z-[100] ${
        isVisible ? 'flex' : 'hidden'
      }`}
      style={{ gridTemplateRows: 'auto 1fr' }}
    >
      <div className="flex flex-col gap-4 lg:gap-2 p-4">
        <Link href="/" className="flex items-center gap-2" prefetch={false}>
          <Back className="w-6 h-6 lg:hidden" />
          <h2 className="font-semibold">Messages</h2>
        </Link>
        {isLogged && (
          <div className="relative flex items-center">
            <Search className="absolute left-3 text-primary" />
            <input
              type="search"
              placeholder="Search..."
              className="bg-secondary rounded-xl w-full pl-10 pr-3 py-3.5 text-sm placeholder:text-primary text-white border border-primary"
              readOnly={Boolean(!data?.length)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>
      <div className="cy-conversations-list flex flex-col overflow-y-auto pb-16">{renderList()}</div>
    </div>
  );
};
